import React, {useEffect, useState} from 'react';
import Draggable from 'react-draggable';
import {Button, Card, CardHeader, CardBody, Collapse} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    CopyPlusIcon,
    LanguagesIcon,
    LightbulbIcon
} from 'lucide-react';
import {useMutation, useQuery} from 'react-query';
import {toast} from 'react-toastify';
import {v4 as uuidv4} from 'uuid';
import useLocalState from '../../lib/useLocalState';
import isNullOrWhiteSpace from '../../lib/isNullOrWhiteSpace';
import unknownErrorToString from '../../lib/unknownErrorToString';
import useTranslate from '../../api/assistant/useTranslate';
import useRemixMix from '../../api/mixes/useRemixMix.ts';
import {
    SavedTranslationRow,
    TranslationMode,
    TranslationSource,
    useDeleteSavedTranslation,
    useSavedTranslations,
    useTranslationsForPage
} from '../../api/assistant/savedTranslations.ts';
import useFetchProjectSceneScriptAndHtml from '../../api/scene/useFetchProjectSceneScriptAndHtml.ts';
import extractBlocksProjectText from '../blocks/extractBlocksProjectText.ts';
import AssistantOutput from '../assistant/AssistantOutput';
import ButtonAsync from '../shared/ButtonAsync';
import {TranslationProjectInfo} from './TranslationContext';
import {ProjectDataIndex, ProjectTabType} from '../../api/types';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import LanguagePicker from './LanguagePicker.tsx';
import SavedTranslationsList from './SavedTranslationsList.tsx';
import {modeLabel} from './savedTranslationFormat.ts';
import SavedTranslationOutput from './SavedTranslationOutput.tsx';

const extractTextFromDOM = (): string => {
    const tabElement = document.querySelector('div.tab-content');
    const mainElement = document.querySelector('main');
    const fullscreenElement = document.getElementById('fullscreen-content');
    const targetElement = tabElement || mainElement || fullscreenElement;

    if (!targetElement) {
        console.error(
            'No tab-content, main, or fullscreen-content element found'
        );
        return '';
    }

    const isElementVisible = (element: Element): boolean => {
        const style = window.getComputedStyle(element);
        return (
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            style.opacity !== '0'
        );
    };

    const textNodes: string[] = [];
    const walker = document.createTreeWalker(
        targetElement,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: node => {
                // Check if the node is a descendant of DraggableTranslationPanel
                let parent = node.parentElement;
                while (parent) {
                    if (parent.id === 'DraggableTranslationPanel') {
                        return NodeFilter.FILTER_REJECT;
                    }
                    // Check if the parent element is visible
                    if (!isElementVisible(parent)) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    parent = parent.parentElement;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        }
    );

    let node;
    while ((node = walker.nextNode())) {
        const trimmedText = node.textContent?.trim();
        if (trimmedText) {
            textNodes.push(trimmedText);
        }
    }

    return textNodes.join('\n');
};

interface TranslationAdviceProps {
    isOpen: boolean;
    toggle: () => void;
    onAccept: () => void;
    isAccepted: boolean;
}

const TranslationAdvice: React.FC<TranslationAdviceProps> = ({
    isOpen,
    toggle,
    onAccept,
    isAccepted
}) => (
    <div className="mb-3">
        <Button color="link" onClick={toggle} className="p-0">
            <Icon
                icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
                className="me-2"
            />
            Translation Advice
        </Button>
        <Collapse isOpen={isOpen}>
            <div className="mt-2 p-3 border rounded">
                <p>
                    This translation is powered by AI technology. While we
                    strive for accuracy, please be aware of the following
                    limitations:
                </p>
                <ul>
                    <li>
                        AI translations may contain errors or
                        misinterpretations.
                    </li>
                    <li>
                        The AI model does not account for regional language
                        variations or dialects.
                    </li>
                    <li>
                        Cultural nuances and context-specific meanings may be
                        lost in translation.
                    </li>
                    <li>
                        For languages with multiple variants (e.g., Te Reo
                        Māori), the AI may not accurately represent local or
                        indigenous forms.
                    </li>
                </ul>
                <p>
                    We recognize that language is deeply connected to culture
                    and identity. This AI translation tool is not intended to
                    replace or diminish the importance of indigenous languages
                    or regional variations.
                </p>
                <p>
                    The translated content should be used as a general guide
                    only and not relied upon for critical information. For
                    important communications or to capture the full cultural
                    context, we recommend consulting with fluent speakers or
                    professional translators.
                </p>
                <p>
                    <Link to="/project/language-translation?tab=project">
                        Learn more ...
                    </Link>
                </p>
                {!isAccepted && (
                    <div className="d-flex justify-content-end">
                        <Button
                            color="primary"
                            onClick={onAccept}
                            className="mt-2"
                        >
                            I Understand
                        </Button>
                    </div>
                )}
            </div>
        </Collapse>
    </div>
);

// What the panel shows below the form: translations streaming in, and saved
// ones opened from the list.
type LiveOutput = {
    kind: 'live';
    outputId: string;
    language: string;
    mode: TranslationMode;
};
type PanelOutput = LiveOutput | {kind: 'saved'; row: SavedTranslationRow};

interface DraggableTranslationPanelProps {
    onClose: () => void;
    projectInfo: TranslationProjectInfo | null;
}

const DraggableTranslationPanel: React.FC<DraggableTranslationPanelProps> = ({
    onClose,
    projectInfo
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [adviceAccepted, setAdviceAccepted] = useLocalState<boolean>(
        'translationAdviceAccepted',
        false
    );
    const [showAdvice, setShowAdvice] = useState<boolean>(!adviceAccepted);

    const handleAdviceAccept = (): void => {
        setAdviceAccepted(true);
        setShowAdvice(false);
    };

    const toggleAdvice = (): void => setShowAdvice(!showAdvice);

    const translate = useTranslate();
    const translateMutation = useMutation(translate, {
        onError: (error: unknown) => {
            toast.error('Error. ' + unknownErrorToString(error));
        }
    });

    const [selectedLanguage, setSelectedLanguage] = useLocalState(
        'selectedLanguage',
        ''
    );
    const [otherLanguage, setOtherLanguage] = useLocalState(
        'otherLanguage',
        ''
    );
    const language =
        selectedLanguage === 'Other' ? otherLanguage : selectedLanguage;

    const [outputs, setOutputs] = useState<PanelOutput[]>([]);
    const [showAllSaved, setShowAllSaved] = useState(false);

    const removeOutput = (outputToRemove: PanelOutput) => {
        setOutputs(prevState =>
            prevState.filter(output => output != outputToRemove)
        );
    };

    const tabType = projectInfo?.projectTabType;
    const onProjectTab =
        !!projectInfo?.mixId && projectInfo.dataIndex !== ProjectDataIndex.Zero;
    // A vibe-coded tab runs in a sandboxed frame the page can't read, so it is
    // explained from its code, or copied in the chosen language, instead.
    const onVibeCodeTab = onProjectTab && tabType === ProjectTabType.VibeCode;
    const onBlocksTab = onProjectTab && tabType === ProjectTabType.Blocks;

    // Blocks tabs translate the project's authored content (say-text, sprite/
    // costume/sound names, messages, variables) extracted from the stored
    // project document — the DOM here is editor chrome, not content.
    const fetchBlocksScene = useFetchProjectSceneScriptAndHtml(
        projectInfo?.mixId ?? '',
        projectInfo?.dataIndex ?? ProjectDataIndex.Zero
    );

    const blocksProjectText = async (): Promise<string | null> => {
        if (!onBlocksTab) {
            return null;
        }

        try {
            const scene = await fetchBlocksScene();
            return extractBlocksProjectText(scene?.sceneScript);
        } catch {
            return null; // fall back to the generic DOM extraction
        }
    };

    // What a translation of this page is made from. Whiteboards, content and
    // vibe-coded tabs are read on the server from what is stored; a content
    // package by its id; anything else from the text on screen.
    const sourceFor = async (
        mode: TranslationMode
    ): Promise<TranslationSource> => {
        const contentPackageElement = document.querySelector(
            'div[data-contentpackageid]'
        );

        if (contentPackageElement) {
            return {
                text: null,
                mixId: null,
                dataIndex: null,
                contentPackageId: contentPackageElement.getAttribute(
                    'data-contentpackageid'
                )
            };
        }

        const readOnServer =
            onProjectTab &&
            (tabType === ProjectTabType.Whiteboard ||
                tabType === ProjectTabType.Content ||
                (tabType === ProjectTabType.VibeCode &&
                    mode === TranslationMode.Guide));

        if (readOnServer) {
            return {
                text: null,
                mixId: projectInfo!.mixId,
                dataIndex: projectInfo!.dataIndex,
                contentPackageId: null
            };
        }

        return {
            text: (await blocksProjectText()) ?? extractTextFromDOM(),
            mixId: null,
            dataIndex: null,
            contentPackageId: null
        };
    };

    // Where translations of this page are filed, and what the list calls it.
    const pageKey = onProjectTab
        ? `project:${projectInfo!.mixId}:${projectInfo!.dataIndex}`
        : `page:${location.pathname}`;
    const pageTitle =
        (projectInfo?.projectName
            ? [
                  projectInfo.projectName,
                  onProjectTab ? projectInfo.tabTitle : null
              ]
                  .filter(Boolean)
                  .join(' · ')
            : document.querySelector('h1')?.textContent?.trim()) ||
        document.title;

    // The page's saved translations. A guide's source stands in for both
    // kinds: they only differ on vibe-coded tabs, where only guides are made.
    const fetchForPage = useTranslationsForPage();
    const {data: savedForPage, refetch: refetchSavedForPage} = useQuery(
        ['translations-for-page', pageKey],
        async () =>
            fetchForPage({
                pageKey,
                ...(await sourceFor(TranslationMode.Guide))
            }),
        {enabled: adviceAccepted}
    );

    const fetchAllSaved = useSavedTranslations();
    const {data: allSaved, refetch: refetchAllSaved} = useQuery(
        ['saved-translations'],
        fetchAllSaved,
        {enabled: adviceAccepted && showAllSaved}
    );

    // The delete hook is bound to an id, so a delete runs once it is set.
    const [deletingId, setDeletingId] = useState('');
    const deleteSaved = useDeleteSavedTranslation(deletingId);
    const deleteMutation = useMutation(deleteSaved, {
        onSuccess: () => {
            void refetchSavedForPage();
            if (showAllSaved) {
                void refetchAllSaved();
            }
        },
        onError: (error: unknown) => {
            toast.error('Error. ' + unknownErrorToString(error));
        }
    });

    useEffect(() => {
        if (deletingId) {
            deleteMutation.mutate(undefined);
            setDeletingId('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletingId]);

    const deleteRow = (row: SavedTranslationRow) => {
        setOutputs(prev =>
            prev.filter(o => !(o.kind === 'saved' && o.row.id === row.id))
        );
        setDeletingId(row.id);
    };

    const openSaved = (row: SavedTranslationRow) =>
        setOutputs(prev =>
            prev.some(o => o.kind === 'saved' && o.row.id === row.id)
                ? prev
                : [...prev, {kind: 'saved', row}]
        );

    // Adds the output first; it sends the request once it is listening.
    const run = (mode: TranslationMode, forLanguage = language) => {
        if (isNullOrWhiteSpace(forLanguage)) {
            return;
        }
        setOutputs(prev => [
            ...prev,
            {kind: 'live', outputId: uuidv4(), language: forLanguage, mode}
        ]);
    };

    const startLive = async (output: LiveOutput) => {
        const source = await sourceFor(output.mode);
        await translateMutation.mutateAsync({
            outputId: output.outputId,
            language: output.language,
            mode: output.mode,
            pageKey,
            title: pageTitle,
            ...source
        });
    };

    // "Make my own copy": a remix of this tab, which opens in the Vibe window
    // and translates itself there (see ProjectPage's vibeLang).
    const remixMix = useRemixMix();
    const copyMutation = useMutation(remixMix, {
        onSuccess: result => {
            if (result) {
                navigate(
                    `/project/${result.id}?tab=${
                        result.tab ?? 'project'
                    }&vibe=1&vibeLang=${encodeURIComponent(language)}`
                );
            }
        },
        onError: (error: unknown) => {
            toast.error(
                'Unable to make a copy. ' + unknownErrorToString(error)
            );
        }
    });

    const makeCopy = async () => {
        // A tab's default title ("Project", "Vibe Code", "Tab 3") says
        // nothing about it; the project's own name does.
        const tabTitle = projectInfo?.tabTitle;
        const isDefaultTitle =
            !tabTitle || /^(Project|Vibe Code|Tab \d)$/i.test(tabTitle);
        const named = isDefaultTitle
            ? projectInfo?.projectName || 'Copy'
            : tabTitle;
        const name = `${named} (${language})`;
        await copyMutation.mutateAsync({
            id: projectInfo!.mixId,
            sourceTab: projectInfo!.dataIndex,
            name: name.slice(0, 64),
            description: '',
            tags: [],
            thumbnail: null,
            replacements: []
        });
    };

    const languageMissing = !adviceAccepted || isNullOrWhiteSpace(language);
    const savedRows = showAllSaved ? allSaved : savedForPage;

    return (
        <Draggable handle=".handle">
            <Card
                id="DraggableTranslationPanel"
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '600px',
                    zIndex: 1000
                }}
            >
                <CardHeader
                    className="handle"
                    style={{
                        cursor: 'move',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <div>
                        <Icon icon={LanguagesIcon} className="me-2" />
                        Translate
                    </div>
                    <Button close onClick={onClose} />
                </CardHeader>
                <CardBody>
                    <TranslationAdvice
                        isOpen={showAdvice}
                        toggle={toggleAdvice}
                        onAccept={handleAdviceAccept}
                        isAccepted={adviceAccepted}
                    />

                    {adviceAccepted && (
                        <>
                            <LanguagePicker
                                selected={selectedLanguage}
                                other={otherLanguage}
                                onSelectedChange={setSelectedLanguage}
                                onOtherChange={setOtherLanguage}
                            />
                            <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                                {onVibeCodeTab ? (
                                    <>
                                        <ButtonAsync
                                            type="button"
                                            color="secondary"
                                            onClick={makeCopy}
                                            disabled={languageMissing}
                                        >
                                            <Icon icon={CopyPlusIcon} />{' '}
                                            {isNullOrWhiteSpace(language)
                                                ? 'Make my own copy in this language'
                                                : `Make my own copy in ${language}`}
                                        </ButtonAsync>
                                        <Button
                                            type="button"
                                            color="primary"
                                            onClick={() =>
                                                run(TranslationMode.Guide)
                                            }
                                            disabled={languageMissing}
                                            title="Says what this is and how to use it, with the words on screen"
                                        >
                                            <Icon icon={LightbulbIcon} />{' '}
                                            Explain this
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        {onBlocksTab && (
                                            <Button
                                                type="button"
                                                color="secondary"
                                                onClick={() =>
                                                    run(TranslationMode.Guide)
                                                }
                                                disabled={languageMissing}
                                            >
                                                <Icon icon={LightbulbIcon} />{' '}
                                                Explain this
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            color="primary"
                                            onClick={() =>
                                                run(TranslationMode.Text)
                                            }
                                            disabled={languageMissing}
                                        >
                                            Translate
                                        </Button>
                                    </>
                                )}
                            </div>

                            <section className="mt-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-2">
                                        {showAllSaved
                                            ? 'All saved translations'
                                            : 'Saved for this page'}
                                    </h5>
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() =>
                                            setShowAllSaved(!showAllSaved)
                                        }
                                    >
                                        {showAllSaved
                                            ? 'Just this page'
                                            : 'All saved'}
                                    </Button>
                                </div>
                                {savedRows && savedRows.length > 0 ? (
                                    <SavedTranslationsList
                                        rows={savedRows}
                                        onOpen={openSaved}
                                        onDelete={deleteRow}
                                        showPage={showAllSaved}
                                    />
                                ) : (
                                    <p className="small text-muted mb-0">
                                        {showAllSaved
                                            ? 'Nothing saved yet. '
                                            : 'Nothing saved for this page yet. '}
                                        Translations you make are kept here so
                                        you can open them again.
                                    </p>
                                )}
                            </section>

                            <div className="mt-4">
                                {outputs.map(output =>
                                    output.kind === 'live' ? (
                                        <AssistantOutput
                                            key={output.outputId}
                                            id={output.outputId}
                                            title={`${
                                                output.language
                                            } ${modeLabel(
                                                output.mode
                                            ).toLowerCase()}`}
                                            onReady={() =>
                                                void startLive(output)
                                            }
                                            onComplete={() =>
                                                void refetchSavedForPage()
                                            }
                                            onRemove={() =>
                                                removeOutput(output)
                                            }
                                        />
                                    ) : (
                                        <SavedTranslationOutput
                                            key={output.row.id}
                                            row={output.row}
                                            onRemove={() =>
                                                removeOutput(output)
                                            }
                                            onTranslateAgain={
                                                output.row.pageKey === pageKey
                                                    ? () =>
                                                          run(
                                                              output.row.mode,
                                                              output.row
                                                                  .language
                                                          )
                                                    : undefined
                                            }
                                        />
                                    )
                                )}
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        </Draggable>
    );
};

export default DraggableTranslationPanel;
